import * as pdfjsLib from 'pdfjs-dist'
import type { ResumeExperience, ResumeEducation } from '../types/database'

// Set up PDF.js worker
// Use unpkg CDN which is more reliable than cdnjs for PDF.js workers
// unpkg serves files directly from npm packages, ensuring version compatibility
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`
}

export interface ParsedResume {
  name: string
  title: string
  email: string
  phone: string
  location: string
  summary: string
  experience: Omit<ResumeExperience, 'id' | 'order'>[]
  education: Omit<ResumeEducation, 'id' | 'order'>[]
  skills: string[]
}

/**
 * Extract text from PDF
 */
export async function extractTextFromPdf(pdfUrl: string): Promise<string> {
  try {
    // Validate URL
    if (!pdfUrl || !pdfUrl.trim()) {
      throw new Error('PDF URL is required')
    }

    // Check if URL is valid
    try {
      new URL(pdfUrl)
    } catch {
      throw new Error('Invalid PDF URL format')
    }

    // Fetch PDF with better error handling
    let response: Response
    try {
      response = await fetch(pdfUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': 'application/pdf,application/octet-stream,*/*'
        }
      })
    } catch (fetchError: any) {
      if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
        throw new Error('Failed to fetch PDF: CORS error or network issue. Please ensure the PDF URL is accessible.')
      }
      throw new Error(`Failed to fetch PDF: ${fetchError.message}`)
    }

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('PDF file not found. Please check if the file was uploaded correctly.')
      } else if (response.status === 403) {
        throw new Error('Access denied to PDF file. Please check file permissions.')
      } else {
        throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`)
      }
    }

    // Check content type
    const contentType = response.headers.get('content-type')
    if (contentType && !contentType.includes('pdf') && !contentType.includes('octet-stream')) {
      console.warn(`Unexpected content type: ${contentType}. Proceeding anyway...`)
    }

    const blob = await response.blob()
    
    // Validate blob size
    if (blob.size === 0) {
      throw new Error('PDF file is empty')
    }

    const arrayBuffer = await blob.arrayBuffer()
    
    // Load PDF document
    let pdf: any
    try {
      const loadingTask = pdfjsLib.getDocument({ 
        data: arrayBuffer,
        verbosity: 0 // Suppress warnings
      })
      pdf = await loadingTask.promise
    } catch (pdfError: any) {
      if (pdfError.name === 'InvalidPDFException' || pdfError.message?.includes('Invalid PDF')) {
        throw new Error('Invalid PDF file. Please ensure the file is a valid PDF document.')
      }
      throw new Error(`Failed to load PDF: ${pdfError.message || 'Unknown error'}`)
    }

    if (!pdf || pdf.numPages === 0) {
      throw new Error('PDF has no pages')
    }
    
    // Extract text from all pages with better structure preservation
    let fullText = ''
    try {
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const textContent = await page.getTextContent()
        
        // Preserve structure by detecting line breaks and spacing
        let pageText = ''
        let lastY = null
        let lastItem: any = null
        
        for (const item of textContent.items) {
          const currentY = item.transform ? item.transform[5] : null
          const currentX = item.transform ? item.transform[4] : null
          const lastX = lastItem?.transform ? lastItem.transform[4] : null
          
          // Detect line breaks based on Y position changes (threshold: 3px difference)
          if (lastY !== null && currentY !== null && Math.abs(currentY - lastY) > 3) {
            pageText += '\n'
          } else if (lastItem && item.str && lastItem.str) {
            // Add space between words on same line if needed
            if (lastX !== null && currentX !== null) {
              const spaceWidth = (currentX - lastX) - (lastItem.width || 0)
              // If items are far apart (more than 2px), add space
              if (spaceWidth > 2 && !lastItem.str.endsWith(' ') && !item.str.startsWith(' ')) {
                pageText += ' '
              }
            } else if (!lastItem.str.endsWith(' ') && !item.str.startsWith(' ')) {
              // Fallback: add space if items don't already have spacing
              pageText += ' '
            }
          }
          
          pageText += item.str || ''
          lastY = currentY
          lastItem = item
        }
        
        fullText += pageText + '\n\n'
      }
    } catch (pageError: any) {
      throw new Error(`Failed to extract text from PDF pages: ${pageError.message || 'Unknown error'}`)
    }

    if (!fullText.trim()) {
      throw new Error('No text could be extracted from the PDF. The PDF may be image-based or encrypted.')
    }
    
    return fullText
  } catch (error: any) {
    console.error('Error extracting text from PDF:', error)
    
    // Provide user-friendly error messages
    if (error.message) {
      throw error
    } else if (error.name === 'NetworkError') {
      throw new Error('Network error while fetching PDF. Please check your internet connection.')
    } else {
      throw new Error(`Error extracting text from PDF: ${error.message || 'Unknown error occurred'}`)
    }
  }
}

/**
 * Parse resume text into structured data using AI
 * Falls back to manual parsing if AI extraction fails
 */
export async function parseResumeText(text: string): Promise<ParsedResume> {
  // Try AI extraction first
  try {
    const aiParsed = await parseResumeTextWithAI(text)
    if (aiParsed && (aiParsed.experience.length > 0 || aiParsed.education.length > 0 || aiParsed.name)) {
      return aiParsed
    }
  } catch (error) {
    console.warn('AI parsing failed, falling back to manual parsing:', error)
  }
  
  // Fallback to manual parsing
  return parseResumeTextManual(text)
}

/**
 * Format experience descriptions to combine split bullet points and split combined ones
 * Handles both: lines split across multiple lines AND multiple bullets in one string
 */
function formatExperienceDescriptions(experiences: Omit<ResumeExperience, 'id' | 'order'>[]): Omit<ResumeExperience, 'id' | 'order'>[] {
  return experiences.map(exp => {
    if (!exp.description) return exp
    
    // First, try to split by newlines
    const lines = exp.description.split('\n').map(l => l.trim()).filter(l => l.length > 0)
    const bulletPattern = /^[•\-\u2022\u2023\u25E6]\s*/
    const formattedBullets: string[] = []
    let currentBullet = ''
    
    // If we only have one line, it might be multiple bullet points combined
    if (lines.length === 1) {
      const singleLine = lines[0]
      // Try to detect multiple bullet points in a single line
      // Look for patterns like: "Sentence. Capital letter" or "Sentence, Capital verb"
      // Split on sentence boundaries followed by capital letters (likely new bullet point)
      const sentences = singleLine.split(/(?<=[.!])\s+(?=[A-Z])/).filter(s => s.trim().length > 0)
      
      // If we found multiple sentences, treat each as a potential bullet point
      if (sentences.length > 1) {
        // Further refine: look for common bullet point patterns
        // Bullet points often start with action verbs (Built, Improved, Owned, etc.)
        const actionVerbs = /\b(Built|Improved|Owned|Architected|Developed|Created|Designed|Implemented|Optimized|Managed|Led|Delivered|Reduced|Increased|Enhanced|Established|Launched|Deployed|Maintained|Collaborated|Mentored|Trained|Wrote|Configured|Integrated|Automated|Streamlined|Refactored|Migrated|Upgraded|Secured|Monitored|Analyzed|Resolved|Troubleshot|Documented|Tested|Reviewed|Coordinated|Planned|Executed|Evaluated|Identified|Recommended|Presented|Facilitated)\b/i
        
        for (let i = 0; i < sentences.length; i++) {
          const sentence = sentences[i].trim()
          // If sentence starts with an action verb or is clearly a separate point, it's a new bullet
          if (actionVerbs.test(sentence) || i === 0) {
            if (currentBullet && i > 0) {
              formattedBullets.push(currentBullet.trim())
              currentBullet = sentence
            } else {
              currentBullet = sentence
            }
          } else {
            // Continuation of previous bullet
            currentBullet = (currentBullet ? currentBullet + ' ' : '') + sentence
          }
        }
      } else {
        // Single sentence/paragraph, treat as one bullet
        currentBullet = singleLine
      }
    } else {
      // Multiple lines - process each
      for (const line of lines) {
        const isBulletStart = line.match(bulletPattern) || line.startsWith('- ')
        
        if (isBulletStart) {
          // Save previous bullet if exists
          if (currentBullet) {
            formattedBullets.push(currentBullet.trim())
          }
          // Start new bullet
          currentBullet = line.replace(bulletPattern, '').replace(/^-\s*/, '').trim()
        } else if (currentBullet) {
          // Continuation of current bullet - append with space
          currentBullet = currentBullet + ' ' + line.trim()
        } else {
          // No current bullet, start one
          currentBullet = line.trim()
        }
      }
    }
    
    // Add last bullet
    if (currentBullet) {
      formattedBullets.push(currentBullet.trim())
    }
    
    return {
      ...exp,
      description: formattedBullets.join('\n')
    }
  })
}

/**
 * Parse resume text using free AI services via backend API
 * Falls back to client-side Hugging Face API if backend fails
 */
async function parseResumeTextWithAI(text: string): Promise<ParsedResume> {
  // Try backend API first (better for CORS and reliability)
  try {
    const API_URL = import.meta.env.VITE_API_URL || '/api'
    const response = await fetch(`${API_URL}/parse-resume`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: text.substring(0, 5000) }),
    })

    if (response.ok) {
      const data = await response.json()
      if (data && (data.experience?.length > 0 || data.education?.length > 0 || data.name)) {
        return {
          name: data.name || '',
          title: data.title || '',
          email: data.email || '',
          phone: data.phone || '',
          location: data.location || '',
          summary: data.summary || '',
          experience: formatExperienceDescriptions(Array.isArray(data.experience) ? data.experience : []),
          education: Array.isArray(data.education) ? data.education : [],
          skills: Array.isArray(data.skills) ? data.skills : []
        }
      }
    } else {
      // Non-OK response - API might be unavailable or error occurred
      if (response.status === 500 || response.status === 503) {
        const errorData = await response.json().catch(() => ({}))
        console.warn('Backend AI parsing returned error, trying direct API:', errorData)
      } else if (response.status >= 400) {
        // Client or server error - try direct API
        console.warn(`Backend API returned ${response.status}, trying direct API...`)
      }
    }
  } catch (error: any) {
    // Network error - API server likely not running
    if (error?.name === 'TypeError' && error?.message?.includes('fetch')) {
      console.warn('Backend API not available (server may not be running). Trying direct API...')
      console.warn('💡 Tip: Run `npm run dev:vercel` instead of `npm run dev` to enable API endpoints')
    } else {
      console.warn('Backend AI parsing failed, trying direct API:', error)
    }
  }

  // Fallback: Try direct Hugging Face API (may have CORS issues)
  const prompt = `Extract resume information as JSON only:
{
  "name": "Full Name",
  "title": "Job Title",
  "email": "email@example.com",
  "phone": "phone",
  "location": "City, State",
  "summary": "summary",
  "experience": [{"title": "Job", "company": "Company", "years": "Dates", "description": "Description", "skills": []}],
  "education": [{"degree": "Degree", "university": "University", "years": "Dates", "details": "Details"}],
  "skills": ["skill1", "skill2"]
}

Resume: ${text.substring(0, 3000)}`

  try {
    const response = await fetch('https://api-inference.huggingface.co/models/mistralai/Mistral-7B-Instruct-v0.2', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        inputs: prompt,
        parameters: { max_new_tokens: 2000, temperature: 0.1, return_full_text: false }
      }),
    })

    if (response.ok) {
      const data = await response.json()
      const extracted = extractJsonFromResponse(data)
      if (extracted) {
        return extracted
      }
    }
  } catch (error) {
    console.warn('Direct AI API failed:', error)
  }

  throw new Error('AI parsing unavailable')
}

function extractJsonFromResponse(data: any): ParsedResume | null {
  let extractedText = ''
  
  if (Array.isArray(data)) {
    if (data[0]?.generated_text) {
      extractedText = data[0].generated_text
    } else if (data[0]?.text) {
      extractedText = data[0].text
    }
  } else if (typeof data === 'string') {
    extractedText = data
  } else if (data.generated_text) {
    extractedText = data.generated_text
  }

  const jsonMatch = extractedText.match(/\{[\s\S]*\}/)
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0])
      if (parsed && (parsed.experience || parsed.education || parsed.name)) {
        return {
          name: parsed.name || '',
          title: parsed.title || '',
          email: parsed.email || '',
          phone: parsed.phone || '',
          location: parsed.location || '',
          summary: parsed.summary || '',
          experience: Array.isArray(parsed.experience) ? parsed.experience : [],
          education: Array.isArray(parsed.education) ? parsed.education : [],
          skills: Array.isArray(parsed.skills) ? parsed.skills : []
        }
      }
    } catch (parseError) {
      console.error('JSON parse error:', parseError)
    }
  }
  
  return null
}


/**
 * Manual parsing fallback (original implementation)
 */
function parseResumeTextManual(text: string): ParsedResume {
  const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0)
  
  const result: ParsedResume = {
    name: '',
    title: '',
    email: '',
    phone: '',
    location: '',
    summary: '',
    experience: [],
    education: [],
    skills: []
  }
  
  // Extract email
  const emailMatch = text.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/)
  if (emailMatch) {
    result.email = emailMatch[0]
  }
  
  // Extract phone (various formats including $+1(808)$ format)
  const phonePatterns = [
    /\$?\+?\d{1,3}[-.\s()]?\d{3}[-.\s()]?\d{3}[-.\s]?\d{4}/, // $+1(808)$ 232-1931 format
    /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/, // Standard formats
    /\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/ // Simple format
  ]
  for (const pattern of phonePatterns) {
    const phoneMatch = text.match(pattern)
    if (phoneMatch) {
      result.phone = phoneMatch[0].replace(/\$/g, '').trim()
      break
    }
  }
  
  // Find name (usually first line, often in larger font or centered)
  // Look for name pattern: typically 2-4 capitalized words
  if (lines.length > 0) {
    const firstLine = lines[0]
    // Name pattern: 2-4 words, mostly capitalized, no email/phone patterns
    const namePattern = /^[A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,3}$/
    if (namePattern.test(firstLine) && !firstLine.includes('@') && !/\d{3}/.test(firstLine)) {
      result.name = firstLine
    } else if (!firstLine.includes('@') && !/\d{3}/.test(firstLine) && firstLine.length < 50 && firstLine.split(' ').length <= 4) {
      // Fallback: if it looks like a name (short, no special chars)
      result.name = firstLine
    }
  }
  
  // Find title (usually second or third line, often after name)
  // Look for professional titles
  const titleKeywords = ['developer', 'engineer', 'designer', 'manager', 'analyst', 'specialist', 'consultant', 'architect', 'lead', 'senior', 'junior']
  for (let i = 1; i < Math.min(4, lines.length); i++) {
    const line = lines[i].toLowerCase()
    if (titleKeywords.some(keyword => line.includes(keyword)) && !line.includes('@') && !/\d{3}/.test(line)) {
      result.title = lines[i]
      break
    }
  }
  
  // If no title found by keywords, use second line if it looks like a title
  if (!result.title && lines.length > 1) {
    const secondLine = lines[1]
    if (!secondLine.includes('@') && !/\d{3}/.test(secondLine) && secondLine.length < 100 && secondLine.length > 5) {
      result.title = secondLine
    }
  }
  
  // Find location (look for common location patterns)
  // Format: "City, State, Country" or "City, State" or "City, Country"
  const locationPatterns = [
    /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),\s*[A-Z]{2},\s*[A-Z]+/, // City, State, Country
    /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),\s*[A-Z]{2}/, // City, State
    /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),\s*[A-Z][a-z]+/, // City, Country
    /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+[A-Z]{2}/, // City State
  ]
  
  for (const pattern of locationPatterns) {
    const match = text.match(pattern)
    if (match) {
      result.location = match[0]
      break
    }
  }
  
  // Find sections
  let currentSection = ''
  let currentContent: string[] = []
  const projectsContent: string[] = [] // Track projects separately for skills extraction
  
  const sectionKeywords = {
    experience: ['experience', 'work experience', 'employment', 'work history', 'professional experience', 'professional', 'employment history', 'career'],
    education: ['education', 'academic', 'university', 'college', 'degree', 'academic background', 'qualifications'],
    skills: ['skills', 'technical skills', 'competencies', 'expertise', 'technologies', 'technical expertise', 'proficiencies', 'tools', 'languages'],
    summary: ['summary', 'about', 'profile', 'objective', 'overview', 'professional summary', 'executive summary'],
    projects: ['projects', 'project', 'portfolio', 'work samples']
  }
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const lineLower = line.toLowerCase()
    
    // Check if this line is a section header (often all caps or with special formatting)
    let foundSection = false
    for (const [section, keywords] of Object.entries(sectionKeywords)) {
      for (const keyword of keywords) {
        // Match section headers (often all caps, short lines, or markdown headers)
        const isHeader = (lineLower.includes(keyword) && line.length < 50) || 
                        (line === line.toUpperCase() && lineLower.includes(keyword)) ||
                        (line.startsWith('#') && lineLower.includes(keyword))
        
        if (isHeader) {
          // Save previous section content
          if (currentSection && currentContent.length > 0) {
            saveSectionContent(currentSection, currentContent.join('\n'), result)
          }
          currentSection = section
          currentContent = []
          if (section === 'projects') {
            projectsContent.length = 0 // Reset projects content
          }
          foundSection = true
          break
        }
      }
      if (foundSection) break
    }
    
    if (!foundSection && currentSection) {
      currentContent.push(lines[i])
      // Also track projects content for skills extraction
      if (currentSection === 'projects') {
        projectsContent.push(lines[i])
      }
    }
  }
  
  // Save last section
  if (currentSection && currentContent.length > 0) {
    saveSectionContent(currentSection, currentContent.join('\n'), result)
  }
  
  // Extract skills from projects section if skills section wasn't found
  if (projectsContent.length > 0 && result.skills.length === 0) {
    const projectsText = projectsContent.join('\n')
    const extractedSkills = parseSkills(projectsText)
    result.skills = [...new Set([...result.skills, ...extractedSkills])]
  }
  
  // If no sections found, try to parse the whole text
  if (!result.experience.length && !result.education.length && !result.skills.length) {
    parseWithoutSections(text, result)
  }
  
  return result
}

function saveSectionContent(section: string, content: string, result: ParsedResume) {
  switch (section) {
    case 'summary':
      result.summary = content.substring(0, 500) // Limit summary length
      break
    case 'experience':
      result.experience = parseExperience(content)
      break
    case 'education':
      result.education = parseEducation(content)
      break
    case 'skills':
      result.skills = parseSkills(content)
      break
  }
}

function parseExperience(content: string): Omit<ResumeExperience, 'id' | 'order'>[] {
  const experiences: Omit<ResumeExperience, 'id' | 'order'>[] = []
  const lines = content.split('\n').map(l => l.trim()).filter(l => l.length > 0)
  
  let currentExp: Partial<Omit<ResumeExperience, 'id' | 'order'>> = {}
  let descriptionLines: string[] = []
  
  // Pattern for date ranges: "Month YYYY - Present" or "Month YYYY - Month YYYY" or "YYYY - YYYY"
  const yearPattern = /((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4}\s*[-–—]\s*(?:Present|Current|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4}))|(\d{4}\s*[-–—]\s*(?:Present|Current|\d{4}))/i
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const nextLine = lines[i + 1] || ''
    const lineAfterNext = lines[i + 2] || ''
    
    // Pattern 1: Company — Location (e.g., "Volonte — Belleville, IL, USA")
    // Followed by date range on next line
    // Followed by ## Job Title
    const companyLocationPattern = /^(.+?)\s*[—–-]\s*(.+)$/
    const companyMatch = line.match(companyLocationPattern)
    
    if (companyMatch) {
      // Save previous experience
      if (currentExp.title) {
        experiences.push({
          title: currentExp.title || '',
          company: currentExp.company || '',
          years: currentExp.years || '',
          description: descriptionLines.join('\n'),
          skills: currentExp.skills || []
        })
      }
      
      currentExp = {
        company: companyMatch[1].trim()
      }
      descriptionLines = []
      
      // Check next line for date range
      if (nextLine && yearPattern.test(nextLine)) {
        currentExp.years = nextLine.match(yearPattern)?.[0] || ''
        i++ // Skip date line
        
        // Check line after date for job title (## Title format)
        if (lineAfterNext && (lineAfterNext.startsWith('##') || lineAfterNext.startsWith('#'))) {
          currentExp.title = lineAfterNext.replace(/^#+\s*/, '').trim()
          i++ // Skip title line
        }
      } else {
        // Date might be on same line or title might be on next line
        if (nextLine && (nextLine.startsWith('##') || nextLine.startsWith('#'))) {
          currentExp.title = nextLine.replace(/^#+\s*/, '').trim()
          i++ // Skip title line
          // Date might be after title
          if (lineAfterNext && yearPattern.test(lineAfterNext)) {
            currentExp.years = lineAfterNext.match(yearPattern)?.[0] || ''
            i++ // Skip date line
          }
        }
      }
      continue
    }
    
    // Pattern 2: Title | Company | Date (common format)
    const pipePattern = /^(.+?)\s*\|\s*(.+?)(?:\s*\|\s*(.+))?$/
    const pipeMatch = line.match(pipePattern)
    if (pipeMatch) {
      if (currentExp.title) {
        experiences.push({
          title: currentExp.title || '',
          company: currentExp.company || '',
          years: currentExp.years || '',
          description: descriptionLines.join('\n'),
          skills: currentExp.skills || []
        })
      }
      
      currentExp = { title: pipeMatch[1].trim() }
      currentExp.company = pipeMatch[2].trim()
      if (pipeMatch[3]) {
        const yearMatch = pipeMatch[3].match(yearPattern)
        if (yearMatch) currentExp.years = yearMatch[0]
      }
      descriptionLines = []
      continue
    }
    
    // Pattern 3: Title at Company | Date
    const atPattern = /^(.+?)\s+at\s+(.+?)(?:\s*\|\s*(.+))?$/i
    const atMatch = line.match(atPattern)
    if (atMatch) {
      if (currentExp.title) {
        experiences.push({
          title: currentExp.title || '',
          company: currentExp.company || '',
          years: currentExp.years || '',
          description: descriptionLines.join('\n'),
          skills: currentExp.skills || []
        })
      }
      
      currentExp = { title: atMatch[1].trim() }
      currentExp.company = atMatch[2].trim()
      if (atMatch[3]) {
        const yearMatch = atMatch[3].match(yearPattern)
        if (yearMatch) currentExp.years = yearMatch[0]
      }
      descriptionLines = []
      continue
    }
    
    // Pattern 4: Job title as markdown header (## Title) - might come after company/location
    if ((line.startsWith('##') || line.startsWith('#')) && !currentExp.title && currentExp.company) {
      currentExp.title = line.replace(/^#+\s*/, '').trim()
      continue
    }
    
    // Pattern 5: Date range on its own line (when company was already set)
    if (yearPattern.test(line) && currentExp.company && !currentExp.years) {
      currentExp.years = line.match(yearPattern)?.[0] || ''
      continue
    }
    
    // Collect description lines (bullet points or paragraphs)
    if (currentExp.title || currentExp.company) {
      const bulletPattern = /^[•\-\u2022\u2023\u25E6]\s*/
      const isBulletLine = line.match(bulletPattern) || line.startsWith('- ')
      
      if (isBulletLine) {
        // New bullet point - start a new entry
        const cleanLine = line.replace(bulletPattern, '').replace(/^-\s*/, '').trim()
        if (cleanLine.length > 0) {
          descriptionLines.push(cleanLine)
        }
      } else if (descriptionLines.length > 0 && line.length > 0) {
        // Continuation of previous bullet point - append to last entry
        // Only append if it doesn't look like a new section (no date, no company pattern, etc.)
        if (!yearPattern.test(line) && !line.includes('|') && !line.toLowerCase().includes('at ') && 
            !line.match(companyLocationPattern) && !line.startsWith('##') && !line.startsWith('#')) {
          // Append to last bullet point with a space
          const lastIndex = descriptionLines.length - 1
          descriptionLines[lastIndex] = descriptionLines[lastIndex] + ' ' + line.trim()
        }
      } else if (line.length > 20 && !yearPattern.test(line) && !line.includes('|') && 
                 !line.toLowerCase().includes('at ') && !line.match(companyLocationPattern) && 
                 !line.startsWith('##') && !line.startsWith('#')) {
        // Regular paragraph text (not a header, not a company line, not a date)
        descriptionLines.push(line)
      }
    }
  }
  
  // Save last experience
  if (currentExp.title || currentExp.company) {
    experiences.push({
      title: currentExp.title || '',
      company: currentExp.company || '',
      years: currentExp.years || '',
      description: descriptionLines.join('\n'),
      skills: currentExp.skills || []
    })
  }
  
  return experiences
}

function parseEducation(content: string): Omit<ResumeEducation, 'id' | 'order'>[] {
  const educations: Omit<ResumeEducation, 'id' | 'order'>[] = []
  const lines = content.split('\n').map(l => l.trim()).filter(l => l.length > 0)
  
  let currentEdu: Partial<Omit<ResumeEducation, 'id' | 'order'>> = {}
  const yearPattern = /((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4}\s*[-–—]\s*(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4})|(\d{4}\s*[-–—]\s*\d{4})/i
  const degreePattern = /(Bachelor|Master|PhD|Doctorate|Associate|Diploma|Certificate).*?(?:in|of|,|\s)/i
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const nextLine = lines[i + 1] || ''
    const lineAfterNext = lines[i + 2] || ''
    
    // Pattern 1: University name on its own line
    // Followed by date range on next line
    // Followed by degree info on next line(s)
    const looksLikeUniversity = line.length > 5 && line.length < 100 && 
                                (line.includes('University') || line.includes('College') || 
                                 line.includes('Institute') || line.includes('School'))
    
    if (looksLikeUniversity && !currentEdu.university) {
      // Save previous education if exists
      if (currentEdu.university || currentEdu.degree) {
        educations.push({
          degree: currentEdu.degree || '',
          university: currentEdu.university || '',
          years: currentEdu.years || '',
          details: currentEdu.details || ''
        })
      }
      
      currentEdu = { university: line }
      
      // Check next line for date range
      if (nextLine && yearPattern.test(nextLine)) {
        currentEdu.years = nextLine.match(yearPattern)?.[0] || ''
        i++ // Skip date line
        
        // Check line after date for degree info
        if (lineAfterNext) {
          const degreeMatch = lineAfterNext.match(degreePattern)
          if (degreeMatch) {
            currentEdu.degree = lineAfterNext.split('|')[0].trim() // Take part before | if exists
            i++ // Skip degree line
            
            // Check for additional details (GPA, honors, etc.)
            if (i + 1 < lines.length) {
              const detailsLine = lines[i + 1]
              if (detailsLine.includes('GPA') || detailsLine.includes('Cum Laude') || detailsLine.includes("Dean's")) {
                currentEdu.details = detailsLine
                i++ // Skip details line
              }
            }
          } else {
            // Might be degree info without keyword
            currentEdu.degree = lineAfterNext.split('|')[0].trim()
            i++
          }
        }
      } else {
        // Date might be on same line or degree might be on next line
        if (nextLine) {
          const degreeMatch = nextLine.match(degreePattern)
          if (degreeMatch) {
            currentEdu.degree = nextLine.split('|')[0].trim()
            i++ // Skip degree line
            // Check for date on same line or next
            if (yearPattern.test(nextLine)) {
              currentEdu.years = nextLine.match(yearPattern)?.[0] || ''
            } else if (lineAfterNext && yearPattern.test(lineAfterNext)) {
              currentEdu.years = lineAfterNext.match(yearPattern)?.[0] || ''
              i++ // Skip date line
            }
          }
        }
      }
      continue
    }
    
    // Pattern 2: Degree on its own line (fallback)
    const degreeMatch = line.match(degreePattern)
    if (degreeMatch && !currentEdu.degree) {
      if (currentEdu.university) {
        educations.push({
          degree: currentEdu.degree || '',
          university: currentEdu.university || '',
          years: currentEdu.years || '',
          details: currentEdu.details || ''
        })
      }
      
      currentEdu = { degree: line.split('|')[0].trim() }
      
      // Check next lines for university and years
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1]
        
        if (yearPattern.test(nextLine)) {
          currentEdu.years = nextLine.match(yearPattern)?.[0] || ''
          if (i + 2 < lines.length && !yearPattern.test(lines[i + 2])) {
            currentEdu.university = lines[i + 2]
            i += 2
          } else {
            i++
          }
        } else if (nextLine.length < 100 && (nextLine.includes('University') || nextLine.includes('College'))) {
          currentEdu.university = nextLine
          i++
        }
      }
      continue
    }
    
    // Collect additional details (GPA, honors, etc.)
    if (currentEdu.degree || currentEdu.university) {
      if (line.includes('GPA') || line.includes('Cum Laude') || line.includes("Dean's") || 
          line.includes('Magna') || line.includes('Summa') || line.includes('Honors')) {
        if (!currentEdu.details) {
          currentEdu.details = line
        } else {
          currentEdu.details += ' | ' + line
        }
      }
    }
  }
  
  // Save last education
  if (currentEdu.university || currentEdu.degree) {
    educations.push({
      degree: currentEdu.degree || '',
      university: currentEdu.university || '',
      years: currentEdu.years || '',
      details: currentEdu.details || ''
    })
  }
  
  return educations
}

function parseSkills(content: string): string[] {
  const skills: string[] = []
  const lines = content.split('\n').map(l => l.trim()).filter(l => l.length > 0)
  
  // Common tech stack patterns from projects section
  // Format: "Project — Tech1, Tech2, Tech3" or "Project | Tech1, Tech2"
  const techStackPattern = /[—–-]\s*([A-Za-z0-9.+#\s,]+)$/
  
  for (const line of lines) {
    // Check if line contains tech stack (after — or |)
    const techMatch = line.match(techStackPattern)
    if (techMatch) {
      const techStack = techMatch[1]
      // Split by comma, semicolon, or space
      const items = techStack.split(/[,;]|\s+/).map(item => item.trim()).filter(item => item.length > 0)
      for (const item of items) {
        // Filter out common non-tech words
        const lowerItem = item.toLowerCase()
        if (item.length > 1 && item.length < 50 && 
            !item.match(/^\d+$/) && 
            !['and', 'with', 'using', 'via', 'for'].includes(lowerItem) &&
            !lowerItem.includes('built') && !lowerItem.includes('created')) {
          skills.push(item)
        }
      }
    } else {
      // Regular skill line - split by common delimiters
      const items = line.split(/[,;|•\-]|\sand\s/i).map(item => item.trim()).filter(item => item.length > 0)
      
      for (const item of items) {
        if (item.length > 1 && item.length < 50 && !item.match(/^\d+$/)) {
          skills.push(item)
        }
      }
    }
  }
  
  // Remove duplicates and limit
  return [...new Set(skills)].slice(0, 50)
}

function parseWithoutSections(text: string, result: ParsedResume) {
  // Fallback parsing when no clear sections are found
  // Try to find skills in the text (common tech terms)
  const techTerms = [
    'JavaScript', 'TypeScript', 'React', 'Node.js', 'Python', 'Java', 'C++', 'C#',
    'HTML', 'CSS', 'SQL', 'MongoDB', 'PostgreSQL', 'AWS', 'Docker', 'Git',
    'Angular', 'Vue', 'Next.js', 'Express', 'Django', 'Flask', 'Spring', 'Laravel',
    'Supabase', 'PostGIS', 'D3.js', 'React Native', 'Flask', 'Prophet', 'Textract',
    'TypeScript', 'PostgreSQL', 'Stripe', 'CI/CD', 'GitHub Actions'
  ]
  
  for (const term of techTerms) {
    if (text.includes(term) && !result.skills.includes(term)) {
      result.skills.push(term)
    }
  }
  
  // Also extract skills from project tech stacks
  const projectTechPattern = /[—–-]\s*([A-Za-z0-9.+#\s,]+)$/gm
  let match
  while ((match = projectTechPattern.exec(text)) !== null) {
    const techStack = match[1]
    const items = techStack.split(/[,;]|\s+/).map(item => item.trim())
    for (const item of items) {
      if (item.length > 1 && item.length < 50 && !item.match(/^\d+$/) && !result.skills.includes(item)) {
        // Check if it's a known tech term
        if (techTerms.some(term => item.includes(term) || term.includes(item))) {
          result.skills.push(item)
        }
      }
    }
  }
}

